/**
 * ChronicleKeyPlayers — DEV-1352. The Key Players section on a chronicle's
 * About tab, with per-card add/edit via KeyPlayerTray (Skip's request: a "+"
 * to add and an edit button on each card, instead of the bulk inline editor).
 *
 * On a Topic Tracker the section is titled "Leading Characters" and each card
 * carries a why-they-matter caption; on a Project Chronicle it stays "Key
 * Players". Both reuse the shared FlipTeamMemberCard for training-ground parity.
 *
 * Empty + author → friendly prompt. Empty + viewer → renders nothing.
 */

import React, { useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import FlipTeamMemberCard from '../TeamMemberCard/FlipTeamMemberCard';
import KeyPlayerTray from './KeyPlayerTray';
import type { ChronicleResponse, ChronicleTeamCreditResponse } from '../../api/chronicles';

interface Props {
  chronicle: ChronicleResponse;
  canEdit: boolean;
  /** Fired after a tray save/delete so the parent refetches. */
  onChanged: () => void;
}

const ChronicleKeyPlayers: React.FC<Props> = ({ chronicle, canEdit, onChanged }) => {
  const { t } = useAppTranslation('pages');
  const isTracker = chronicle.categoryKey === 'topic_tracker';

  const [trayOpen, setTrayOpen] = useState(false);
  const [editing, setEditing] = useState<ChronicleTeamCreditResponse | null>(null);

  const players = useMemo(
    () => [...(chronicle.teamCredits ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [chronicle.teamCredits],
  );

  const heading = isTracker
    ? t('chronicleKeyPlayers.headingTracker', { defaultValue: 'Leading Characters' })
    : t('chronicleKeyPlayers.heading', { defaultValue: 'Key Players' });

  const whyLabel = isTracker
    ? t('chronicleKeyPlayers.whyTracker', { defaultValue: 'Why they matter' })
    : t('chronicleKeyPlayers.why', { defaultValue: 'Role on this project' });
  const whyPlaceholder = isTracker
    ? t('chronicleKeyPlayers.whyTrackerPlaceholder', { defaultValue: 'How is this person, company, or community relevant to the topic?' })
    : t('chronicleKeyPlayers.whyPlaceholder', { defaultValue: 'What did they contribute?' });

  // Nothing to show and can't edit → render nothing.
  if (players.length === 0 && !canEdit) return null;

  const openAdd = () => { setEditing(null); setTrayOpen(true); };
  const openEdit = (c: ChronicleTeamCreditResponse) => { setEditing(c); setTrayOpen(true); };

  const addBtnStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-default)',
    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: 13,
  };

  return (
    <section aria-label={heading}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBlockEnd: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{heading}</h2>
        {canEdit && (
          <button type="button" style={addBtnStyle} onClick={openAdd}>
            <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>+</span>
            {t('chronicleKeyPlayers.add', { defaultValue: 'Add' })}
          </button>
        )}
      </header>

      {players.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 14, lineHeight: 1.6 }}>
          {isTracker
            ? t('chronicleKeyPlayers.emptyTracker', { defaultValue: 'Add the people, companies, or communities at the center of this topic and why each one matters.' })
            : t('chronicleKeyPlayers.empty', { defaultValue: 'Add the people behind this project and what each one contributed.' })}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
          {players.map((tc) => (
            <div key={tc.creditGuid} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ position: 'relative' }}>
                <FlipTeamMemberCard
                  displayName={tc.personName}
                  photoUrl={tc.manualPhotoUrl}
                  roleTitle={tc.roleTitle}
                  linkedUserGuid={tc.userGuid}
                  userGuid={tc.userGuid}
                  contactProfileUrl={tc.externalProfileUrl}
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => openEdit(tc)}
                    aria-label={t('chronicleKeyPlayers.editAria', { defaultValue: 'Edit {{name}}', name: tc.personName })}
                    title={t('chronicleKeyPlayers.editTitle', { defaultValue: 'Edit' })}
                    style={{
                      position: 'absolute',
                      insetBlockStart: 6,
                      insetInlineEnd: 6,
                      inlineSize: 28,
                      blockSize: 28,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 6,
                      border: '1px solid var(--border-default)',
                      background: 'var(--bg-elevated)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      zIndex: 2,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                )}
              </div>
              {tc.description && (
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, textAlign: 'center' }}>
                  {tc.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <KeyPlayerTray
        open={trayOpen}
        onClose={() => setTrayOpen(false)}
        chronicle={chronicle}
        editingCredit={editing}
        whyLabel={whyLabel}
        whyPlaceholder={whyPlaceholder}
        onSaved={onChanged}
      />
    </section>
  );
};

export default ChronicleKeyPlayers;
